'use client'

import { useQuery } from '@tanstack/react-query'
import Link from 'next/link'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { useTrendingPosts } from '@/hooks/use-trending-posts'

import { UserAvatarPopover } from '@/components/common/modal/avatar-hover'
import { ResolvedAvatarImage } from '@/components/common/resolved-media-image'
import Skeleton from '@/components/Skeleton'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import { useMemePostModal } from '@/contexts/meme-post-modal-context'
import memeService from '@/services/meme.service'
import { formatCount } from '@/utils/formatCount'
import { hideEmail } from '@/utils/hidden-email'

import { UserAuth } from '@/types/user.type'

const getRankBadgeColor = (rank: number) => {
  switch (rank) {
    case 1:
      return 'bg-yellow-100 text-yellow-800'
    case 2:
      return 'bg-gray-100 text-gray-800'
    case 3:
      return 'bg-orange-100 text-orange-800'
    default:
      return 'bg-blue-100 text-blue-800'
  }
}

export function FeaturedMemes() {
  const { data: trendingPosts, isLoading, error } = useTrendingPosts({ limit: 6 })
  const { openModal } = useMemePostModal('MemePostModal')

  const handleMemeClick = async (memeId: string) => {
    try {
      const meme = await memeService.getMemeDetail(memeId)
      openModal(meme)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to open meme modal:', error)
    }
  }

  return (
    <Card className="bg-white shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold">
          Meme nổi bật hôm nay
          {isLoading && <span className="text-sm font-normal text-gray-500 ml-2">(đang tải...)</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {error && (
          <div className="text-sm text-red-600 p-3 border-b border-gray-100">
            Không thể tải meme nổi bật. Hiển thị dữ liệu mặc định.
          </div>
        )}
        {trendingPosts?.map((meme, index) => (
          <div
            key={meme.id || index}
            className="flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0 cursor-pointer"
            onClick={() => handleMemeClick(meme.id)}
          >
            <Badge
              variant="secondary"
              className={`${getRankBadgeColor(index + 1)} font-bold min-w-[24px] h-6 flex items-center justify-center`}
            >
              #{index + 1}
            </Badge>
            {meme.isAnonymous ? (
              <Avatar className="h-10 w-10">
                <ResolvedAvatarImage avatar={meme.user?.avatar} alt={`${meme.user?.firstName} ${meme.user?.lastName}`} />
                <AvatarFallback>
                  {meme.user?.firstName[0]}
                  {meme.user?.lastName[0]}
                </AvatarFallback>
              </Avatar>
            ) : (
              <UserAvatarPopover user={meme.user} />
            )}
            <div className="flex-1 min-w-0">
              <div
                className="text-sm text-gray-700 line-clamp-2 overflow-hidden text-ellipsis featured-memes"
                dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(meme.content) }}
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              />
              {meme.user && (
                <Link href={`/user/${meme.user?.profileId || ''}`} passHref>
                  <div className="text-xs text-gray-500 truncate">
                    {meme.user.firstName} {meme.user.lastName}
                  </div>
                </Link>
              )}
            </div>
          </div>
        ))}
      </CardContent>
      <style jsx global>{`
        .featured-memes .ql-hashtag {
          display: inline-block !important;
          line-height: 1 !important;
          padding: 2px 4px !important;
          border-radius: 4px !important;
          color: #1d4ed8 !important;
          background-color: #e0f2fe !important;
          text-decoration: none !important;
          cursor: pointer;
        }
        .featured-memes .ql-hashtag:hover {
          background-color: #bae6fd !important;
        }
        .featured-memes .ql-hashtag:active {
          background-color: #7dd3fc !important;
        }

        .featured-memes .custom-emoji-inline {
          display: inline-block !important;
          width: 24px !important;
          height: 24px !important;
          line-height: 1 !important;
          vertical-align: text-bottom !important; /* or 'middle' if you prefer */
          margin: 0 2px !important;
          border-radius: 4px !important;
          object-fit: cover !important;
          pointer-events: none; /* optional: so clicking doesn't move the caret oddly */
        }
      `}</style>
    </Card>
  )
}

export function Leaderboard({ limit }: { limit?: number }) {
  const {
    data: leaderboard,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['meme-leaderboard', limit],
    queryFn: () => memeService.getTopUsersByEngagementForMemePost(limit),
  })
  if (error) return null
  return (
    <Card className="bg-white shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold">Bảng xếp hạng</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading && <Skeleton className="h-10 w-full" />}
        {leaderboard?.map((user, index) => (
          <div
            key={index}
            className="flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0"
          >
            <Badge
              variant="secondary"
              className={`${getRankBadgeColor(user.rank)} font-bold min-w-[24px] h-6 flex items-center justify-center`}
            >
              #{user.rank}
            </Badge>
            <UserAvatarPopover user={user as unknown as UserAuth} />

            <Link href={`/user/${user?.profileId || ''}`} passHref>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate hover:underline">
                  {user.firstName} {user.lastName}
                </div>
                <div className="text-xs text-gray-700 line-clamp-1 overflow-hidden text-ellipsis">
                  {hideEmail(user.email)}
                </div>
              </div>
            </Link>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

export function ChannelLeaderboard({ channelId, limit = 5 }: { channelId: string; limit?: number }) {
  const {
    data: leaderboard,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['channel-leaderboard', channelId],
    queryFn: () => memeService.getChannelUserStats(channelId),
    enabled: !!channelId,
  })

  if (error) return null
  if (!channelId) return null

  return (
    <Card className="bg-white shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold">Bảng xếp hạng kênh</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">Top người dùng hoạt động nhất</p>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading && (
          <div className="space-y-2 p-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}
        {!isLoading && leaderboard && leaderboard.length === 0 && (
          <div className="p-4 text-center text-sm text-muted-foreground">Chưa có dữ liệu hoạt động</div>
        )}
        {!isLoading &&
          (typeof limit === 'number' ? leaderboard?.slice(0, limit) : leaderboard)?.map((user, index) => (
            <div
              key={user.id || index}
              className="p-3 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-center gap-3">
                <Badge
                  variant="secondary"
                  className={`${getRankBadgeColor(index + 1)} font-bold min-w-[24px] h-6 flex items-center justify-center`}
                >
                  #{index + 1}
                </Badge>
                <UserAvatarPopover user={user as unknown as UserAuth} />

                <Link href={`/user/${user?.profileId || ''}`} passHref className="flex-1 min-w-0">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate hover:underline">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{hideEmail(user.email)}</div>
                  </div>
                </Link>
              </div>

              <div className="flex gap-2 flex-wrap mt-2 pl-9">
                <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                  <span className="font-semibold text-gray-900">{formatCount(user.totalPosts)}</span> bài viết
                </span>
                <span className="text-gray-300">•</span>
                <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                  <span className="font-semibold text-gray-900">{formatCount(user.totalComments)}</span> bình luận
                </span>
                <span className="text-gray-300">•</span>
                <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                  <span className="font-semibold text-gray-900">{formatCount(user.totalEngagement)}</span> tương tác
                </span>
              </div>
            </div>
          ))}
      </CardContent>
    </Card>
  )
}

type RightSidebarMemeProps = {
  channelId?: string
  showRanking?: boolean
  showTrending?: boolean
  rankingLimit?: number
}

export default function RightSidebarMeme({
  channelId,
  showRanking = true,
  showTrending = false,
  rankingLimit,
}: RightSidebarMemeProps) {
  return (
    <div className="space-y-4">
      {showTrending && <FeaturedMemes />}
      {showRanking &&
        (channelId ? (
          <ChannelLeaderboard channelId={channelId} limit={rankingLimit} />
        ) : (
          <Leaderboard limit={rankingLimit} />
        ))}
      <style jsx global>{`
        .ql-hashtag {
          color: #1d4ed8 !important;
          background-color: #e0f2fe !important;
          font-weight: 600 !important;
          border-radius: 4px !important;
          padding: 2px 4px !important;
          text-decoration: none !important;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .ql-hashtag:hover {
          background-color: #bae6fd !important;
        }

        .ql-hashtag:active {
          background-color: #7dd3fc !important;
        }
      `}</style>
    </div>
  )
}
